import {
  usePriorityModalMeta,
  useSetPriorityModal,
} from "@/shared/atoms/usePriorityModal";
import { cn } from "@/shared/utils/cn";
import { formatPrice } from "@/shared/utils/helpers";
import { OrderV2 } from "@/shared/v2/queries/interfacesV2";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Icon from "../Icon/Icon";
import Modal, { ModalProps } from "../Modal/Modal";
import useSourceHook from "@/shared/hooks/useSourceHook";
import IconToolTip from "../Icon/IconToolTip";
import useOrderInformationHook from "@/shared/hooks/useOrderInformationHook";
import Order from "../Order/Order";

export interface OrderCancelledByExternalProviderModalMeta {
  order?: OrderV2;
}

const InfoCell = ({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) => (
  <div className="flex min-w-0 flex-col gap-1 flex-1 items-center">
    <span className="font-bold uppercase text-text-tertiary">{label}</span>
    <span className={cn("flex h-7 items-center font-bold", valueClassName)}>
      {value}
    </span>
  </div>
);

const OrderCancelledByExternalProviderModal = (props: ModalProps) => {
  const { t } = useTranslation();
  const [meta] = usePriorityModalMeta();
  const setPriorityModal = useSetPriorityModal();
  const { getSource } = useSourceHook();

  const { order } = (meta as OrderCancelledByExternalProviderModalMeta) ?? {};

  const { source, public_order_number, total_price, daily_receipt_number } =
    useOrderInformationHook(order);

  const handleClose = () => {
    setPriorityModal("", {});
    props.onExit?.();
  };

  return (
    <Modal
      width="30rem"
      closeOnClickOutside={false}
      {...props}
      zIndex={10000001}
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-error-light">
          <Icon variants="info" className="h-10 w-10 fill-error" />
        </div>
        <h3>{t("orderCancelled")}</h3>
        <p className="text-text-tertiary">
          {t("orderCancelledByExternalProviderDescription")}
        </p>
      </div>

      {order && (
        <>
          <div className="mb-2 mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-md border border-border p-4">
            {source && (
              <InfoCell
                label={t("source")}
                value={
                  <IconToolTip
                    text={t(getSource(source).translationKey)}
                    variants={getSource(source).icon}
                    className="size-7 fill-text"
                  />
                }
              />
            )}
            {daily_receipt_number && (
              <InfoCell
                label={t("daily_receipt_number")}
                value={`#${daily_receipt_number}`}
              />
            )}
            {public_order_number && (
              <InfoCell label={t("orderNumber")} value={public_order_number} />
            )}
            {total_price && (
              <InfoCell
                label={t("total")}
                value={formatPrice(total_price)}
                valueClassName="text-primary"
              />
            )}
          </div>
          <Order orders={order} hidePrice={true} />
        </>
      )}

      <Button onClick={handleClose} className="mt-6 w-full">
        {t("close")}
      </Button>
    </Modal>
  );
};

export default OrderCancelledByExternalProviderModal;
